/**
 * Coming Soon / Under Construction landing page.
 * Gates the public marketing site while the team completes launch prep.
 *
 * Also captures pre-launch waitlist signups with a REFERRAL LOOP:
 *   ?ref=XXXXXXXX in the URL is captured and attributed on join.
 *   After joining, subscribers see their own referral link + progress.
 *
 * TOGGLE:
 *   frontend/.env → REACT_APP_COMING_SOON=true    (gate on)
 *                   REACT_APP_COMING_SOON=false   (gate off — normal site)
 *
 * REMOVE AFTER LAUNCH:
 *   1. Set REACT_APP_COMING_SOON=false in frontend/.env (or delete the key)
 *   2. Redeploy
 *   3. All marketing routes resume normal behaviour
 *
 * Existing users can still reach /auth, /platform, /app during the gate.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useAttributionCapture from "@/hooks/useAttributionCapture";
import FirstCloserCard from "@/pages/marketing/FirstCloserCard";
import {
  Sparkles, ArrowRight, Loader2, Check, ShieldCheck, LogIn,
  Users, Copy, Twitter, Linkedin, Mail as MailIcon, Trophy,
  Brain, MessageSquareText, Layers, Share2, Zap, Lock,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Countdown removed 2026-02 when Tracy paused the public launch to run a
// private-beta cohort (partner-run real deals). Leaving `LAUNCH_DATE_ISO`
// null keeps the timer hidden; flip it back to an ISO string to resume.
const LAUNCH_DATE_ISO = null;

// LocalStorage key for persisting the subscriber's referral code across visits.
const LS_REF_CODE = "istc_waitlist_ref_code";

function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!targetIso) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  return useMemo(() => {
    if (!targetIso) return null;
    const target = new Date(targetIso).getTime();
    const diff = Math.max(0, target - now);
    const d = Math.floor(diff / (1000 * 60 * 60 * 24));
    const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const m = Math.floor((diff / (1000 * 60)) % 60);
    const s = Math.floor((diff / 1000) % 60);
    return { d, h, m, s };
  }, [now, targetIso]);
}

const CountTile = ({ n, label, tid }) => (
  <div
    data-testid={tid}
    className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur px-4 py-4 md:px-6 md:py-5 min-w-[72px] md:min-w-[92px] text-center"
  >
    <div className="text-3xl md:text-5xl font-bold text-white tabular-nums leading-none">
      {String(n).padStart(2, "0")}
    </div>
    <div className="mt-1 text-[10px] md:text-xs uppercase tracking-widest text-slate-400">
      {label}
    </div>
  </div>
);

// Read ?ref=XXXX from the URL once on mount.
function readRefFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const r = p.get("ref");
    return r ? r.toUpperCase().slice(0, 12) : null;
  } catch {
    return null;
  }
}

export default function ComingSoonPage() {
  useAttributionCapture();
  const cd = useCountdown(LAUNCH_DATE_ISO);

  // Public waitlist counter (ticker in hero)
  const [totalCount, setTotalCount] = useState(null);

  // Persisted signup — restored on refresh via localStorage
  // (kept for backwards-compat: returning visitors from the legacy email
  //  waitlist still see their ReferralCard on the right column).
  const [subscriber, setSubscriber] = useState(null);
  const [copied, setCopied] = useState(false);
  const refFromUrl = useRef(readRefFromUrl());

  // Fetch public total once, then poll every 30s
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/marketing-site/waitlist/stats`,
        );
        if (!res.ok) return;
        const j = await res.json();
        if (!cancelled) setTotalCount(j.total_count);
      } catch {
        /* silent */
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // On mount: if we have a saved referral_code, refresh self stats
  useEffect(() => {
    const saved = window.localStorage.getItem(LS_REF_CODE);
    if (!saved) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/marketing-site/waitlist/me?code=${saved}`,
        );
        if (!res.ok) return;
        const j = await res.json();
        setSubscriber(j);
      } catch { /* silent */ }
    })();
  }, []);

  // Poll self stats every 20s while success card is shown
  useEffect(() => {
    if (!subscriber?.referral_code) return undefined;
    const id = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/marketing-site/waitlist/me?code=${subscriber.referral_code}`,
        );
        if (!res.ok) return;
        const j = await res.json();
        setSubscriber(j);
      } catch { /* silent */ }
    }, 20000);
    return () => clearInterval(id);
  }, [subscriber?.referral_code]);

  const copyLink = async () => {
    if (!subscriber?.referral_link) return;
    try {
      await navigator.clipboard.writeText(subscriber.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* silent */ }
  };

  const shareText = subscriber
    ? `Just joined the iSpeedToClose waitlist — the AI operating system for real estate investors. Grab your spot: ${subscriber.referral_link}`
    : "";

  return (
    <div
      data-testid="coming-soon-page"
      className="relative min-h-screen text-white overflow-hidden"
      style={{ backgroundColor: "#0B1F3A" }}
    >
      {/* Ambient background layers */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(5,150,105,0.28),transparent_55%)]"></div>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(59,130,246,0.15),transparent_55%)]"></div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(circle at 25% 25%, #fff 1px, transparent 1px), radial-gradient(circle at 75% 75%, #fff 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      ></div>

      <div className="relative flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="max-w-7xl w-full mx-auto px-6 pt-6 md:pt-10 flex items-center justify-between">
          <span
            className="inline-flex items-center px-2.5 py-1.5 rounded-lg shadow-md ring-1 ring-amber-200/40"
            style={{
              backgroundColor: "#FDF8ED",
              backgroundImage: "linear-gradient(135deg, #FEFCF3 0%, #FDF3D8 100%)",
            }}
          >
            <img
              src="/brand/logo-full-color.png?v=1"
              alt="iSpeedToClose"
              className="h-8 md:h-10 w-auto"
              data-testid="coming-soon-logo"
            />
          </span>
          <Link
            to="/login"
            data-testid="coming-soon-signin"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/20 hover:border-emerald-400 hover:text-emerald-300 text-slate-300 text-xs md:text-sm font-semibold px-3 py-1.5 transition-colors"
          >
            <LogIn className="w-3.5 h-3.5" /> Existing user? Sign in
          </Link>
        </header>

        {/* Hero */}
        <main className="flex-1 flex items-center">
          <div className="max-w-7xl w-full mx-auto px-6 py-16 md:py-24 grid md:grid-cols-5 gap-12 items-start">
            <div className="md:col-span-3">
              <div className="inline-flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-emerald-300">
                  <Sparkles className="w-3 h-3" />
                  Private beta · By invite only
                </span>
                {totalCount !== null && totalCount > 0 && (
                  <span
                    data-testid="coming-soon-total-counter"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-slate-200"
                  >
                    <Users className="w-3 h-3 text-emerald-300" />
                    <span className="tabular-nums text-emerald-300">
                      {totalCount.toLocaleString()}
                    </span>
                    <span className="text-slate-400">investors on the list</span>
                  </span>
                )}
              </div>

              {/* Beta / partner-integration disclosure — reassures reviewers
                  (DocuSign Go-Live, Stripe onboarding, etc.) that the
                  underlying platform is fully operational while we finish
                  our public marketing launch. */}
              <div
                data-testid="coming-soon-beta-disclosure"
                className="mt-5 max-w-2xl text-[13px] leading-relaxed text-slate-300/80"
              >
                iSpeedToClose is currently in <span className="text-emerald-300 font-semibold">closed private beta</span> —
                Tracy and a small partner cohort are running the platform through
                real wholesale deals before we open it publicly. Platform + partner
                integrations (Twilio, <a href="/docusign" className="text-emerald-300 underline underline-offset-2 hover:text-emerald-200">DocuSign</a>,
                Stripe) are fully operational. To be considered for the next
                invite wave, reach out to
                <a href="mailto:tperrin@perrinweb.com" className="text-emerald-300 hover:text-emerald-200 ml-1">
                  tperrin@perrinweb.com
                </a>.
              </div>

              <h1
                data-testid="coming-soon-headline"
                className="mt-6 text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[0.95]"
              >
                Something bold<br />
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      "linear-gradient(90deg, #818CF8 0%, #6366F1 40%, #4F46E5 100%)",
                  }}
                >
                  is being battle-tested.
                </span>
              </h1>

              <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-xl leading-relaxed">
                We&rsquo;re running the platform through real deals with a private
                partner cohort before we open the doors. Drop your email and
                you&rsquo;ll be on the shortlist the moment the beta unlocks.
              </p>

              {refFromUrl.current && !subscriber && (
                <div
                  data-testid="coming-soon-invited-banner"
                  className="mt-6 inline-flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-400/30 px-3 py-2 text-sm text-emerald-200"
                >
                  <Sparkles className="w-4 h-4" />
                  You were invited by a friend — you&rsquo;ll be marked as a
                  priority signup.
                </div>
              )}

              {/* Countdown */}
              {cd && (
                <div
                  data-testid="coming-soon-countdown"
                  className="mt-10 flex flex-wrap gap-3 md:gap-4"
                >
                  <CountTile n={cd.d} label="Days"    tid="countdown-days" />
                  <CountTile n={cd.h} label="Hours"   tid="countdown-hours" />
                  <CountTile n={cd.m} label="Minutes" tid="countdown-minutes" />
                  <CountTile n={cd.s} label="Seconds" tid="countdown-seconds" />
                </div>
              )}

              {/* What's inside — feature grid to fill the left column */}
              <div className="mt-10" data-testid="coming-soon-feature-grid">
                <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-emerald-300">
                  <Zap className="w-3.5 h-3.5" />
                  What&rsquo;s waiting on the other side
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    {
                      Icon: Brain,
                      title: "AI Deal Intelligence",
                      desc: "Analyze any property in seconds. Comps, ARV, rehab, MAO — auto-run across five investment strategies.",
                    },
                    {
                      Icon: MessageSquareText,
                      title: "1-Click SMS Blasts",
                      desc: "Verified leads → 100+ personalized SMS sends in one tap, with Claude-powered openers.",
                    },
                    {
                      Icon: Layers,
                      title: "Immutable Deal Timeline",
                      desc: "Every touch, note, and status change on every property — permanent, searchable, court-defensible.",
                    },
                    {
                      Icon: Share2,
                      title: "Deal-Won Share Cards",
                      desc: "Auto-generated proof cards after every closing. Post them once, earn credits on every signup.",
                    },
                  ].map(({ Icon, title, desc }) => (
                    <div
                      key={title}
                      className="rounded-xl bg-white/5 border border-white/10 backdrop-blur px-4 py-3.5 hover:bg-white/[0.07] hover:border-emerald-400/30 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="grid place-items-center h-7 w-7 rounded-lg bg-emerald-500/15 border border-emerald-400/30">
                          <Icon className="w-3.5 h-3.5 text-emerald-300" />
                        </span>
                        <div className="text-[13px] font-bold text-white">{title}</div>
                      </div>
                      <p className="mt-1.5 text-[12px] leading-relaxed text-slate-400">
                        {desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Proof / trust strip */}
              <div className="mt-8 grid grid-cols-3 gap-3" data-testid="coming-soon-proof-strip">
                <div className="rounded-xl bg-white/5 border border-white/10 backdrop-blur px-3 py-3 text-center">
                  <div className="text-2xl font-bold text-emerald-300 tabular-nums">5</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-widest text-slate-400">
                    Strategy engines
                  </div>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 backdrop-blur px-3 py-3 text-center">
                  <div className="text-2xl font-bold text-emerald-300 tabular-nums">$0</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-widest text-slate-400">
                    Setup fee, ever
                  </div>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 backdrop-blur px-3 py-3 text-center">
                  <div className="text-2xl font-bold text-emerald-300 tabular-nums">100</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-widest text-slate-400">
                    Free credits at signup
                  </div>
                </div>
              </div>

              {/* Locked-perk promise */}
              <div className="mt-6 rounded-xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-400/20 px-4 py-3.5 flex items-start gap-3"
                   data-testid="coming-soon-founder-promise">
                <Lock className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
                <div>
                  <div className="text-[13px] font-bold text-white">Your First Closer perks are locked for life.</div>
                  <p className="mt-1 text-[12px] leading-relaxed text-slate-400">
                    Apply now and your 40% discount + welcome credits are yours — even if pricing triples at public launch.
                  </p>
                </div>
              </div>

              {/* Manifesto */}
              <div
                className="mt-6 rounded-2xl bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 backdrop-blur px-6 py-6 md:px-7 md:py-7"
                data-testid="coming-soon-manifesto"
              >
                <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-emerald-300">
                  <Trophy className="w-3.5 h-3.5" />
                  Why we built this
                </div>
                <p className="mt-4 text-[15px] md:text-[16px] leading-relaxed text-slate-200">
                  Every investor tool we&rsquo;ve ever used forced us to bounce between
                  seven tabs, three spreadsheets, and a dying CRM just to run one deal.
                  <span className="text-white font-semibold"> That&rsquo;s over.</span>
                </p>
                <p className="mt-3 text-[13.5px] leading-relaxed text-slate-400">
                  iSpeedToClose 3.0 is a single operating system that thinks like a
                  closer — mobile-first, plugin-based, and wired end-to-end so your
                  properties, contacts, comps, calls, contracts, and closings live
                  in one immutable timeline. No more copy-paste. No more &ldquo;which
                  tab was that in?&rdquo;
                </p>
                <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-3">
                  <span className="grid place-items-center h-9 w-9 rounded-full text-[11px] font-black text-white"
                        style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #EA580C)" }}>
                    TP
                  </span>
                  <div>
                    <div className="text-[13px] font-bold text-white">Tracy Perrin</div>
                    <div className="text-[11px] text-slate-400">iSpeedToClose · 20+ years closing deals</div>
                  </div>
                </div>
              </div>

              {/* Built for — audience callouts */}
              <div className="mt-6" data-testid="coming-soon-built-for">
                <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-emerald-300">
                  <Users className="w-3.5 h-3.5" />
                  Built for
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    "Wholesalers",
                    "Fix-and-Flip Investors",
                    "Buy-and-Hold Investors",
                    "Real Estate Agents",
                    "Coaches & Mentors",
                    "Solo Operators",
                    "Growing Teams",
                  ].map((label) => (
                    <span
                      key={label}
                      className="inline-flex items-center rounded-full bg-white/5 border border-white/10 backdrop-blur px-3 py-1.5 text-[12px] font-semibold text-slate-200 hover:bg-white/[0.08] hover:border-emerald-400/30 transition-colors"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right column — First Closer application + supporting content */}
            <div className="md:col-span-2 space-y-4">
              {/* If a returning visitor already signed up on the legacy waitlist,
                  keep showing their referral share card. Otherwise show the
                  First Closer application (the only sign-up path). */}
              {subscriber ? (
                <div className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl p-6 md:p-8">
                  <ReferralCard
                    subscriber={subscriber}
                    copied={copied}
                    onCopy={copyLink}
                    shareText={shareText}
                  />
                </div>
              ) : (
                <>
                  <FirstCloserCard />

                  {/* How it works — 3-step post-apply flow */}
                  <div
                    className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl p-6 md:p-7"
                    data-testid="coming-soon-how-it-works"
                  >
                    <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-300">
                      What happens next
                    </div>
                    <h3 className="mt-1.5 text-xl font-bold text-white">
                      Inside your dashboard in 60 seconds.
                    </h3>

                    <ol className="mt-5 space-y-4">
                      {[
                        {
                          n: "01",
                          title: "Apply above",
                          desc: "One form, one password. No credit card, ever.",
                        },
                        {
                          n: "02",
                          title: "Auto-login to /v3",
                          desc: "You land on the V3 Dashboard the moment you submit.",
                        },
                        {
                          n: "03",
                          title: "100 credits ready to spend",
                          desc: "Pull your first verified lead + fire an AI-powered SMS blast in one click.",
                        },
                      ].map((s) => (
                        <li key={s.n} className="flex items-start gap-3">
                          <span className="shrink-0 grid place-items-center h-9 w-9 rounded-full text-[11px] font-black text-white"
                                style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #8B5CF6)" }}>
                            {s.n}
                          </span>
                          <div className="flex-1">
                            <div className="text-[13px] font-bold text-white">{s.title}</div>
                            <div className="text-[12px] leading-relaxed text-slate-400">{s.desc}</div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Guarantee strip */}
                  <div
                    className="rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 shadow-2xl p-6"
                    data-testid="coming-soon-guarantee"
                  >
                    <div className="flex items-start gap-3">
                      <span className="grid place-items-center h-10 w-10 rounded-xl bg-emerald-500/15 border border-emerald-400/30 shrink-0">
                        <ShieldCheck className="w-5 h-5 text-emerald-300" />
                      </span>
                      <div>
                        <div className="text-[13px] font-bold text-white">Zero-risk apply</div>
                        <p className="mt-1 text-[12px] leading-relaxed text-slate-400">
                          No credit card required. Cancel your First Closer perks anytime.
                          Your 100 welcome credits never expire.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Mini FAQ */}
                  <div
                    className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl p-6 md:p-7"
                    data-testid="coming-soon-faq"
                  >
                    <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-300">
                      Quick answers
                    </div>
                    <div className="mt-4 space-y-4">
                      {[
                        {
                          q: "Is the product actually live?",
                          a: "Yes — the V3 platform is fully live. First Closers get instant access today; the public launch is what's counting down above.",
                        },
                        {
                          q: "What if I need to cancel later?",
                          a: "Cancel with one click. Your account and any credits you haven't spent stay with you.",
                        },
                        {
                          q: "Is the 40% discount really for life?",
                          a: "Yes — locked to your account permanently, on every paid plan, forever.",
                        },
                      ].map(({ q, a }) => (
                        <div key={q}>
                          <div className="text-[13px] font-bold text-white">{q}</div>
                          <p className="mt-1 text-[12px] leading-relaxed text-slate-400">{a}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="max-w-7xl w-full mx-auto px-6 pb-8 border-t border-white/10 pt-6 text-xs text-slate-500 flex items-center justify-between">
          <div>
            © {new Date().getFullYear()} iSpeedToClose. All rights reserved.
          </div>
          <div className="hidden sm:block">
            The AI Operating System for Real Estate Investors
          </div>
        </footer>
      </div>
    </div>
  );
}

/**
 * ReferralCard — post-signup view with referral link, progress and share.
 */
function ReferralCard({ subscriber, copied, onCopy, shareText }) {
  const {
    referral_code, referral_link, referral_count,
    priority_unlocked, priority_threshold, position, email,
  } = subscriber;

  const progressPct = Math.min(
    100,
    Math.round((referral_count / priority_threshold) * 100),
  );

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referral_link)}`;
  const mailtoUrl = `mailto:?subject=${encodeURIComponent("Check this out — iSpeedToClose")}&body=${encodeURIComponent(shareText)}`;

  return (
    <div data-testid="coming-soon-referral-card">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
          <Check className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="text-lg font-bold leading-tight">You&rsquo;re in.</div>
          <div className="text-xs text-slate-400 leading-tight">
            Position <span className="text-emerald-300 font-mono">#{position.toLocaleString()}</span>
            {" · "}<span className="font-mono">{email}</span>
          </div>
        </div>
      </div>

      {priority_unlocked && (
        <div
          data-testid="coming-soon-priority-badge"
          className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-300"
        >
          <Trophy className="w-3 h-3" /> Priority Access unlocked
        </div>
      )}

      <div className="mt-5">
        <div className="text-xs uppercase tracking-widest text-emerald-400 font-bold">
          Skip the line
        </div>
        <h3 className="mt-1 text-lg font-bold leading-snug">
          Refer {priority_threshold} friends → unlock priority access at launch.
        </h3>

        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-slate-400">
            <span
              data-testid="coming-soon-referral-count"
              className="text-emerald-300 font-bold tabular-nums"
            >
              {referral_count}
            </span>
            {" / "}{priority_threshold} referred
          </span>
          <span className="text-slate-400 tabular-nums">{progressPct}%</span>
        </div>
        <div className="mt-1.5 h-2 w-full rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300 transition-all"
            style={{ width: `${progressPct}%` }}
          ></div>
        </div>

        <div className="mt-5">
          <label className="text-xs text-slate-400">Your referral link</label>
          <div className="mt-1.5 flex items-center gap-2">
            <div
              data-testid="coming-soon-referral-link"
              className="flex-1 truncate rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-xs font-mono text-emerald-200"
              title={referral_link}
            >
              {referral_link}
            </div>
            <button
              type="button"
              onClick={onCopy}
              data-testid="coming-soon-copy-btn"
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-semibold px-3 py-2 transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="coming-soon-share-twitter"
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 text-xs font-semibold px-3 py-2 transition-colors"
          >
            <Twitter className="w-3.5 h-3.5" /> Post
          </a>
          <a
            href={linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="coming-soon-share-linkedin"
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 text-xs font-semibold px-3 py-2 transition-colors"
          >
            <Linkedin className="w-3.5 h-3.5" /> Share
          </a>
          <a
            href={mailtoUrl}
            data-testid="coming-soon-share-email"
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 text-xs font-semibold px-3 py-2 transition-colors"
          >
            <MailIcon className="w-3.5 h-3.5" /> Email
          </a>
        </div>

        <div className="mt-4 text-[11px] text-slate-500 flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3" />
          Your code: <span className="font-mono text-slate-300">{referral_code}</span>
        </div>
      </div>
    </div>
  );
}
